import config from "./ModuleDefinition";
import {
  BOOKABLE_TYPE,
  CODE_TYPE,
  DISCOUNT_CRITERIA,
  LOCALE_ISO_PARSER,
  RATE_TYPE,
  RESTRICTION_CONFIGURATION_TYPE,
  RESTRICTION_TYPE,
  RESTRICTION_VALUE_TYPE,
  WINDOW_TYPE,
} from "../../Common/constants";
import moment from "moment/moment";
import { countryCodes } from "../../../../lib/intlUtils";

function getContractableCondition({ restrictionType, configurationType, duration }) {
  const result = {
    restrictionValue: {
      valueType:
        configurationType === RESTRICTION_CONFIGURATION_TYPE.CONSTANT
          ? RESTRICTION_VALUE_TYPE.DURATION
          : RESTRICTION_VALUE_TYPE.NONE,
    },
    restrictionType,
    configurationType: configurationType || RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT,
    windowType: WINDOW_TYPE.TRAVEL,
  };

  if (configurationType === RESTRICTION_CONFIGURATION_TYPE.CONSTANT && duration) {
    result.restrictionValue.value = {
      duration: parseInt(duration, 10),
      chronoUnit: "DAYS",
    };
  }

  return result;
}

function getPeriodCondition({
  valuesList,
  valueType,
  restrictionType,
  configurationType,
  windowType,
}) {
  const result = {
    restrictionValue: {
      value: [],
      valueType,
    },
    restrictionType,
    configurationType,
    windowType,
  };

  if (valuesList) {
    valuesList.forEach(value => {
      if (!value.startDate || !value.endDate) {
        return null;
      }
      const newValue =
        windowType === WINDOW_TYPE.BOOKING
          ? {
              startDate: moment(value.startDate).format("YYYY-MM-DDT00:00:00"),
              endDate: moment(value.endDate).format("YYYY-MM-DDT23:59:59"),
              weekDays: value.weekDays,
              timeZone: "Europe/Madrid",
            }
          : {
              startDate: moment(value.startDate).format("YYYY-MM-DD"),
              endDate: moment(value.endDate).format("YYYY-MM-DD"),
              weekDays: value.weekDays,
              timeZone: value.timeZone || "Europe/Madrid",
            };

      if (value.startHour) {
        newValue.startHour = moment(value.startHour).format("HH:mm");
      }
      if (value.endHour) {
        newValue.endHour = moment(value.endHour).format("HH:mm");
      }
      if (value.timeZone) {
        newValue.timeZone = value.timeZone;
      }

      result.restrictionValue.value.push(newValue);
    });

    if (result.restrictionValue.value.length < 1) {
      result.restrictionValue.valueType = RESTRICTION_VALUE_TYPE.NONE;
      result.configurationType = RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT;
    }
  }

  return result;
}

function transformEntity({ entity, entitiesLength, formValues }) {
  if (config.hasOwnProperty("translation") && config.translation.hasOwnProperty("entityProperty")) {
    delete entity[config.translation.fieldName];
    const translationField = config.translation.fieldName;
    const translationValues =
      (formValues.hasOwnProperty(translationField) && formValues[translationField]) || [];
    entity[config.translation.entityProperty] = translationValues.map(translation => {
      return {
        locale: translation.locale,
        name: translation.name,
        description: translation.description,
      };
    });
  }
  const isGiveNightRateType = entity.rateType === RATE_TYPE.GIVE_NIGHT;
  const isHighlightMessage = entity.rateType === RATE_TYPE.HIGHLIGHT_MESSAGE;
  if (entity.rateType === RATE_TYPE.PERCENTAGE) {
    entity.rate = entity.ratePercentage;
  } else if (entity.rateType === RATE_TYPE.CONSTANT) {
    entity.rate = entity.rateConstant;
  } else if (
    entity.rateType === RATE_TYPE.GIVE_NIGHT ||
    entity.rateType === RATE_TYPE.HIGHLIGHT_MESSAGE
  ) {
    entity.rate = entity.rateType === RATE_TYPE.GIVE_NIGHT ? 100 : 0;
    entity.rateType = entity.originalRateType;
  }
  const newEntity = {
    isActive: entity.isActive || false,
    isVisible: entity.isVisible || false,
    showPopup: entity.showPopup || false,
    ticker: entity.ticker,
    bookableType: BOOKABLE_TYPE.PROMOTION,
    rate: {
      rateValue: {
        value:
          entity.rateType === RATE_TYPE.PERCENTAGE
            ? entity.rate && Math.abs(parseFloat(entity.rate))
            : {
                amount: entity.rate && Math.abs(parseFloat(entity.rate)),
                currency: "EUR", // TODO set default currency
              },
        valueType:
          entity.rateType === RATE_TYPE.CONSTANT
            ? RESTRICTION_VALUE_TYPE.MONEY
            : RESTRICTION_VALUE_TYPE.DOUBLE,
      },
      rateType: entity.rateType,
    },
    appliesToBookables: {
      INVENTORY: {
        bookableType: BOOKABLE_TYPE.INVENTORY,
        value:
          (entity.appliesToBookables &&
            entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY] &&
            entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY].value) ||
          entity.appliesToInventories ||
          [],
      },
    },
    maxBookableUnits: 0,
    restrictions: {},
    arrangement:
      entity.arrangement || entity.arrangement === 0 ? entity.arrangement : entitiesLength,
    confirmationEmailReceivers: [],
    translations: entity.translations,
    group: entity.group,
    isCombinable: true,
    sharingType: entity.sharingType,
  };

  if (isHighlightMessage) {
    newEntity.icon = entity.icon;
  }

  const discountPeriodicity = entity.discountPeriodicity || entity;
  if (
    isGiveNightRateType &&
    discountPeriodicity.discountedUnits &&
    discountPeriodicity.discountedUnits > 0 &&
    discountPeriodicity.applyEveryNumberOfUnits &&
    discountPeriodicity.applyEveryNumberOfUnits > 0
  ) {
    newEntity.discountPeriodicity = {
      discountedUnits: discountPeriodicity.discountedUnits,
      applyEveryNumberOfUnits: discountPeriodicity.applyEveryNumberOfUnits,
      criteria: discountPeriodicity.criteria || DISCOUNT_CRITERIA.CHEAPEST,
      isRepeatable: discountPeriodicity.isRepeatable || false,
    };
  }

  const contractableConditionsList = [
    RESTRICTION_TYPE.MIN_STAY,
    RESTRICTION_TYPE.MAX_STAY,
    RESTRICTION_TYPE.MIN_NOTICE,
    RESTRICTION_TYPE.MAX_NOTICE,
  ];

  contractableConditionsList.forEach(restrictionKey => {
    newEntity.restrictions[restrictionKey] = getContractableCondition({
      restrictionType: restrictionKey,
      configurationType: entity[restrictionKey]
        ? RESTRICTION_CONFIGURATION_TYPE.CONSTANT
        : RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT,
      duration:
        entity[`${restrictionKey}_duration`] && !isNaN(entity[`${restrictionKey}_duration`])
          ? entity[`${restrictionKey}_duration`]
          : null,
    });
  });

  const periodConditionsList = [
    {
      restrictionType: RESTRICTION_TYPE.OPEN_TRAVEL,
      valueType: RESTRICTION_VALUE_TYPE.TRAVEL_WINDOW_PERIOD_SET,
      windowType: WINDOW_TYPE.TRAVEL,
    },
    {
      restrictionType: RESTRICTION_TYPE.OPEN_BOOKING,
      valueType: RESTRICTION_VALUE_TYPE.BOOKING_WINDOW_PERIOD_SET,
      windowType: WINDOW_TYPE.BOOKING,
    },
    {
      restrictionType: RESTRICTION_TYPE.CLOSE_TRAVEL,
      valueType: RESTRICTION_VALUE_TYPE.TRAVEL_WINDOW_PERIOD_SET,
      windowType: WINDOW_TYPE.TRAVEL,
    },
    {
      restrictionType: RESTRICTION_TYPE.CLOSE_BOOKING,
      valueType: RESTRICTION_VALUE_TYPE.BOOKING_WINDOW_PERIOD_SET,
      windowType: WINDOW_TYPE.BOOKING,
    },
  ];
  // OPEN_TRAVEL is always set from formValues.travelWindow in PromotionsForm.handleSubmit.
  // The fallback covers legacy single-date promotions that don't use the travelWindow list.
  entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL] = entity.restrictions[
    RESTRICTION_TYPE.OPEN_TRAVEL
  ] || [
    {
      startDate: entity[RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE],
      endDate: entity[RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE],
      weekDays: [],
    },
  ];
  periodConditionsList.forEach(restriction => {
    const restrictionType = restriction.restrictionType;

    if (entity.restrictions[restrictionType]) {
      newEntity.restrictions[restrictionType] = getPeriodCondition({
        valuesList: entity.restrictions[restrictionType],
        valueType: restriction.valueType,
        restrictionType: restriction.restrictionType,
        configurationType: entity.restrictions[restrictionType][0]
          ? RESTRICTION_CONFIGURATION_TYPE.CONSTANT
          : RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT,
        windowType: restriction.windowType,
      });
    }
  });
  // Only include LOCK restriction when the checkbox is checked.
  // When unchecked, the key is omitted entirely from the payload.
  if (entity[RESTRICTION_TYPE.LOCK]) {
    newEntity.restrictions[RESTRICTION_TYPE.LOCK] = {
      restrictionValue: {
        valueType: RESTRICTION_VALUE_TYPE.NONE,
      },
      restrictionType: RESTRICTION_TYPE.LOCK,
      configurationType: RESTRICTION_CONFIGURATION_TYPE.OWN,
      windowType: WINDOW_TYPE.TRAVEL,
    };
  }

  if (entity[RESTRICTION_TYPE.DEVICE_TYPE]) {
    const hasDevices =
      entity[RESTRICTION_TYPE.DEVICE_TYPE] && entity[RESTRICTION_TYPE.DEVICE_TYPE].length > 0;

    newEntity.restrictions[RESTRICTION_TYPE.DEVICE_TYPE] = {
      restrictionValue: {
        value: entity[RESTRICTION_TYPE.DEVICE_TYPE],
        valueType: hasDevices
          ? RESTRICTION_VALUE_TYPE.DEVICE_TYPE_SET
          : RESTRICTION_VALUE_TYPE.NONE,
      },
      restrictionType: RESTRICTION_TYPE.DEVICE_TYPE,
      configurationType: hasDevices
        ? RESTRICTION_CONFIGURATION_TYPE.CONSTANT
        : RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT,
      windowType: WINDOW_TYPE.BOOKING,
    };
  }

  if (entity.codeType === CODE_TYPE.UNIVERSAL_CODE && entity[RESTRICTION_TYPE.CODE]) {
    const trimmedCodes =
      entity[RESTRICTION_TYPE.CODE] && entity[RESTRICTION_TYPE.CODE].replace(/\s/g, "");
    newEntity.restrictions[RESTRICTION_TYPE.CODE] = {
      restrictionValue: {
        value: trimmedCodes && trimmedCodes.split(","),
        valueType: RESTRICTION_VALUE_TYPE.CODE_SET,
      },
      restrictionType: RESTRICTION_TYPE.CODE,
      configurationType: RESTRICTION_CONFIGURATION_TYPE.CONSTANT,
      windowType: WINDOW_TYPE.TRAVEL,
    };
  }
  const excludedCountries = entity[RESTRICTION_TYPE.GEOLOCATION];
  newEntity.restrictions[RESTRICTION_TYPE.GEOLOCATION] = {
    restrictionValue: {
      value: excludedCountries.length
        ? countryCodes.filter(it => !excludedCountries.includes(it))
        : [],
      valueType: RESTRICTION_VALUE_TYPE.COUNTRY_CODE_SET,
    },
    restrictionType: RESTRICTION_TYPE.GEOLOCATION,
    configurationType: RESTRICTION_CONFIGURATION_TYPE.CONSTANT,
    windowType: WINDOW_TYPE.BOOKING,
  };
  console.info({
    entity,
    newEntity,
  });
  return newEntity;
}

const NO_VALUE = { valueType: RESTRICTION_VALUE_TYPE.NONE };

// The promo GET response omits `restrictionValue` / `rateValue` whenever the configuration is
// OWN, because RestrictionResponse and RateResponse are annotated `@JsonInclude(NON_NULL)` in
// witbooking-api. The update endpoint, however, rejects a restriction or a rate that lacks them
// (RestrictionRequestDeserializer and BookableRateRequestDeserializer both require the field).
// Restoring them as NONE is faithful: RestrictionRequestFactory.noValueOf and
// RateRequest.noValueOf keep `configurationType` / `rateType` untouched.
function withNoValueFallback(holder, valueProperty) {
  return holder && !holder[valueProperty] ? { ...holder, [valueProperty]: NO_VALUE } : holder;
}

// Builds the update payload straight from the entity as the API returned it, so every setting
// the form is able to save survives an update that only means to flip a flag. Rebuilding the
// payload from form values instead would silently drop whatever the rebuild does not know how
// to reconstruct.
// `id` and `propertyTicker` are the only response-only fields (PromoResponse adds them through
// BookableResponse); any other unknown field would be rejected, since witbooking-api enables
// FAIL_ON_UNKNOWN_PROPERTIES and PromoRequest is not annotated `@JsonIgnoreProperties`.
function entityToUpdateRequest(entity) {
  const { id, propertyTicker, rate, restrictions, ...request } = entity;

  return {
    ...request,
    rate: withNoValueFallback(rate, "rateValue"),
    restrictions: Object.keys(restrictions || {}).reduce(
      (result, restrictionType) => ({
        ...result,
        [restrictionType]: withNoValueFallback(restrictions[restrictionType], "restrictionValue"),
      }),
      {}
    ),
  };
}

function parseBoolean(value) {
  return value ? 1 : 0;
}

function parseRateType(rateType) {
  switch (rateType) {
    case RATE_TYPE.CONSTANT:
      return "fijo";

    case RATE_TYPE.PERCENTAGE:
      return "porcentaje";
    default:
      return null;
  }
}

function parseLockConfigurationType(configurationType) {
  switch (configurationType) {
    case RESTRICTION_CONFIGURATION_TYPE.OWN:
      return 2;

    case RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT:
      return 5;
    default:
      return 0;
  }
}

function parseContractableConditionConfigurationType(configurationType) {
  switch (configurationType) {
    case RESTRICTION_CONFIGURATION_TYPE.CONSTANT:
      return 1;

    case RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT:
    default:
      return 0;
  }
}

function createPromotionData({ id, entity, promotionsList, inventoryIdList }) {
  const lockConfigurationType = parseLockConfigurationType(
    entity.restrictions[RESTRICTION_TYPE.LOCK]
      ? entity.restrictions[RESTRICTION_TYPE.LOCK].configurationType
      : RESTRICTION_CONFIGURATION_TYPE.OWN
  );

  let openTravelStartDate =
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL] &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0]
      ? moment(
          entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0].startDate
        ).format("DD-MM-YYYY")
      : "";

  const openTravelStartDateSplit = openTravelStartDate
    ? openTravelStartDate.split("-")
    : ["", "", ""];

  let openTravelEndDate =
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL] &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0]
      ? moment(
          entity.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL].restrictionValue.value[0].endDate
        ).format("DD-MM-YYYY")
      : "";

  const openTravelEndDateSplit = openTravelEndDate ? openTravelEndDate.split("-") : ["", "", ""];

  let openBookingStartDate =
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value[0]
      ? moment(
          entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value[0].startDate
        ).format("DD-MM-YYYY")
      : "";

  const openBookingStartDateSplit = openBookingStartDate
    ? openBookingStartDate.split("-")
    : ["", "", ""];

  let openBookingEndDate =
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value &&
    entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value[0]
      ? moment(
          entity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING].restrictionValue.value[0].endDate
        ).format("DD-MM-YYYY")
      : "";

  const openBookingEndDateSplit = openBookingEndDate ? openBookingEndDate.split("-") : ["", "", ""];

  const minStay = parseContractableConditionConfigurationType(
    entity.restrictions[RESTRICTION_TYPE.MIN_STAY].configurationType
  );

  const maxStay = parseContractableConditionConfigurationType(
    entity.restrictions[RESTRICTION_TYPE.MAX_STAY].configurationType
  );

  const minRelease = parseContractableConditionConfigurationType(
    entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].configurationType
  );

  const maxRelease = parseContractableConditionConfigurationType(
    entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].configurationType
  );

  const promotionData = {
    _method: id ? "PUT" : "POST",
    "data[Promocione][id]": id ? promotionsList[entity.ticker] : "",
    "data[Promocione][activa]": parseBoolean(entity.isActive),
    "data[Promocione][visible_in_engine]": parseBoolean(entity.isVisible),
    "data[Promocione][codigo]": entity.ticker,
    "data[Promocione][codigo_original]": id ? entity.ticker : "",
    "data[Promocione][ftarifa_type]": parseRateType(entity.rate.rateType),
    "data[Promocione][txt_ftarifa]":
      entity.rate.rateType === RATE_TYPE.CONSTANT
        ? entity.rate.rateValue.value.amount
        : entity.rate.rateValue.value,
    "data[Promocione][ftarifa]": 1,
    "data[Promocione][ftarifa_tip]": 1,
    "data[Promocione][tipotarifa]": 1,
    "data[Promocione][tipopromocion]": "1_0",
    "data[Promocione][fbloqueo]": lockConfigurationType,
    "data[Promocione][txt_fbloqueo]": lockConfigurationType === 2 ? entity.ticker : "",
    "data[Promocione][fbloqueo_tip]": lockConfigurationType,
    "data[Promocione][discountedNights]": entity.discountedUnits || "",
    "data[Promocione][minStayForDiscountedNights]": entity.applyEveryNumberOfUnits || "",
    "data[Promocione][discountedNightCriteria]": entity.criteria || DISCOUNT_CRITERIA.LAST,
    "data[Promocione][stackDiscountedNights]": entity.isRepeatable || 0,
    "data[Promocione][fechaini]": openTravelStartDate,
    "data[Promocione][fechaini][day]": openTravelStartDateSplit[0],
    "data[Promocione][fechaini][month]": openTravelStartDateSplit[1],
    "data[Promocione][fechaini][year]": openTravelStartDateSplit[2],
    "data[Promocione][fechafin]": openTravelEndDate,
    "data[Promocione][fechafin][day]": openTravelEndDateSplit[0],
    "data[Promocione][fechafin][month]": openTravelEndDateSplit[1],
    "data[Promocione][fechafin][year]": openTravelEndDateSplit[2],
    "data[Promocione][fechainicontratacion]": openBookingStartDate,
    "data[Promocione][fechainicontratacion][day]": openBookingStartDateSplit[0],
    "data[Promocione][fechainicontratacion][month]": openBookingStartDateSplit[1],
    "data[Promocione][fechainicontratacion][year]": openBookingStartDateSplit[2],
    "data[Promocione][fechafincontratacion]": openBookingEndDate,
    "data[Promocione][fechafincontratacion][day]": openBookingEndDateSplit[0],
    "data[Promocione][fechafincontratacion][month]": openBookingEndDateSplit[1],
    "data[Promocione][fechafincontratacion][year]": openBookingEndDateSplit[2],
    "data[Promocione][codigopromocional]":
      entity.restrictions[RESTRICTION_TYPE.CODE] &&
      entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue &&
      entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value
        ? entity.restrictions[RESTRICTION_TYPE.CODE].restrictionValue.value.join(",")
        : "",
    "data[Promocione][festancia_minima]": minStay,
    "data[Promocione][txt_festancia_minima]":
      minStay === 0
        ? ""
        : entity.restrictions[RESTRICTION_TYPE.MIN_STAY].restrictionValue.value
        ? entity.restrictions[RESTRICTION_TYPE.MIN_STAY].restrictionValue.value.duration
        : "",
    "data[Promocione][festancia_minima_tip]": minStay,
    "data[Promocione][festancia_maxima]": maxStay,
    "data[Promocione][txt_festancia_maxima]":
      maxStay === 0
        ? ""
        : entity.restrictions[RESTRICTION_TYPE.MAX_STAY].restrictionValue.value
        ? entity.restrictions[RESTRICTION_TYPE.MAX_STAY].restrictionValue.value.duration
        : "",
    "data[Promocione][festancia_maxima_tip]": maxStay,
    "data[Promocione][frelease_minimo]": minRelease,
    "data[Promocione][txt_frelease_minimo]":
      minRelease === 0
        ? ""
        : entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].restrictionValue.value
        ? entity.restrictions[RESTRICTION_TYPE.MIN_NOTICE].restrictionValue.value.duration
        : "",
    "data[Promocione][frelease_minimo_tip]": minRelease,
    "data[Promocione][frelease_maximo]": maxRelease,
    "data[Promocione][txt_frelease_maximo]":
      maxRelease === 0
        ? ""
        : entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].restrictionValue.value
        ? entity.restrictions[RESTRICTION_TYPE.MAX_NOTICE].restrictionValue.value.duration
        : "",
    "data[Promocione][frelease_maximo_tip]": maxRelease,
    "data[Promocione][mensajeinformativo]": "",
    "data[Promocione][grupo]": 1,
  };

  if (!openBookingStartDate || !openBookingEndDate) {
    promotionData["data[Promocione][fechaSinCaducidad]"] = true;
  }

  if (entity.translations) {
    entity.translations.forEach(translation => {
      const locale = LOCALE_ISO_PARSER[translation.locale];

      promotionData[`data[Promocione][name][${locale}]`] = translation.name;
      promotionData[`data[Promocione][descripcion][${locale}]`] = translation.description;
    });
  }

  if (
    entity.appliesToBookables &&
    entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY] &&
    entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY].value
  ) {
    Object.keys(inventoryIdList).forEach(inventory => {
      if (inventoryIdList[inventory]) {
        promotionData[`data[Promocione][habitacionasociada_${inventoryIdList[inventory]}]`] = 0;
      }
    });

    entity.appliesToBookables[BOOKABLE_TYPE.INVENTORY].value.forEach(inventory => {
      if (inventoryIdList[inventory]) {
        promotionData[`data[Promocione][habitacionasociada_${inventoryIdList[inventory]}]`] = 1;
      }
    });
  }

  return promotionData;
}

export { transformEntity, createPromotionData, entityToUpdateRequest };
